import { mimeTypes, type DriveMimeTypes } from '@/shared/constants';
import { type DriveFile, type DriveMimeType } from '@/shared/DriveTypes';
import classNames from 'classnames';
import { type Cache } from 'swr';
import { serialize } from 'swr/_internal';
import type { CookieOptions } from '~/types/cookieStore';
import {
  defaultScope,
  DEV,
  fileScope,
  hasWindow,
  indexName,
  LEGACY,
  MODERN,
  newFileId,
  pageExportMimeTypes,
} from '~/utils/constants';
import { exportFormats, isBun } from './constants';
import { type Params, type PartialToken } from './DriveAPI';
import { entityMap } from './entityMap';
import { concatTo } from './fast';
import gup from './gup';
import { isMarkdownLink } from './isMarkdownLink';
import { log } from './logger';
import { type Profile } from './types';

// It's a lot easier to import in other files using `cx` instead of `classNames`
export const cx: typeof classNames = classNames;

export const delay = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
export const delayWithResolvers = (ms = 0) => {
  const p = Promise.withResolvers();
  setTimeout(p.resolve, ms);
  return p;
};

// CSS
export const em = (px: number, size = 16) => `${px / size}em`;
export const rem = (px: number, size = 16) => `${px / size}rem`;
export const pc = (px: number, size = 16) => `${(px / size) * 100}%`;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
// FIXME: this needs to be removed in favor of the one in sort.ts
export const sortArgsByName = (a: DriveFile, b: DriveFile) => collator.compare(a.name, b.name);
export const sortArrByName = (arr: DriveFile[]) => arr.sort((a, b) => collator.compare(a.name, b.name));

// Runtime only check
export function assertType<T>(_: unknown): asserts _ is T {}
export const isType = <T>(_value: unknown, cond: (() => unknown) | boolean): _value is T =>
  typeof cond === 'boolean' ? cond : Boolean(cond());

export function invariant(condition: unknown, message?: string): asserts condition {
  if (DEV) {
    if (!condition) {
      throw new Error(`Assertion failed${message ? `: ${message}` : ''}`);
    }
    return;
  }
}

export function assert<T>(value: unknown, message?: string): asserts value is NonNullable<T> {
  if (DEV) {
    if (!value) {
      throw new Error(`Assertion failed${message ? `: ${message}` : ''}`);
    }
    return;
  }
}

export const omit = <T extends object, K extends Extract<keyof T, string> = Extract<keyof T, string>>(
  obj: T,
  ...keys: Extract<keyof T, string>[]
): Omit<T, K> => {
  const result = {} as T;
  for (const key in obj) {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  }
  return result;
};

// omit but it returns undefined instead of empty object
export const omitUndef = <T extends object, K extends Extract<keyof T, string> = Extract<keyof T, string>>(
  obj: T,
  ...keys: Extract<keyof T, string>[]
): Omit<T, K> | undefined => {
  const result = omit(obj, ...keys) as T;
  if (Object.keys(result).length === 0) return undefined;
  return result;
};

export const pick = <T extends object, K extends Extract<keyof T, string> = Extract<keyof T, string>>(
  obj: T,
  ...keys: Extract<keyof T, string>[]
): Pick<T, K> => {
  const result = {} as T;
  for (const key of keys) {
    result[key] = obj[key];
  }
  return result;
};

export const uniqueBy = <T, K extends keyof T>(arr: T[], key: K) => {
  const seen = new Set();
  return arr.filter((item) => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

// https://stackoverflow.com/a/51567564/1845423
export const colorIsDark = (color: string | null | undefined, threshold = 156) => {
  if (!color) return false;
  let r: number;
  let g: number;
  let b: number;
  if (color.startsWith('rgb')) {
    const values = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
    r = parseInt(values[0] || '0', 10);
    g = parseInt(values[1] || '0', 10);
    b = parseInt(values[2] || '0', 10);
  } else {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.charAt(0).repeat(2) + hex.charAt(1).repeat(2) + hex.charAt(2).repeat(2);
    }
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  const brightness = r * 299 + g * 587 + b * 114;
  return brightness < threshold * 1000;
};

export const isExportCached = (file: DriveFile | undefined, cache: Cache | undefined) => {
  if (!cache || !file) return false;
  const id = file
    ? isADocument(file)
      ? isAShortcut(file)
        ? file.shortcutDetails.targetId
        : file.id
      : undefined
    : undefined;

  if (!id) return false;
  const data = cache.get(serialize([`/drive/v3/files/${id}/export`, id])?.[0])?.data as string;
  return Boolean(data);
};

export const getId = (file: DriveFile) => ('shortcutDetails' in file ? file.shortcutDetails.targetId : file.id);

export type Shortcut = Extract<DriveFile, { mimeType: DriveMimeTypes['shortcut'] }>;
type ShortcutFolder = Extract<DriveFile, { mimeType: DriveMimeTypes['shortcut'] }> & {
  shortcutDetails: Shortcut['shortcutDetails'] & {
    targetMimeType: DriveMimeTypes['folder'];
  };
};
type Document =
  | Extract<DriveFile, { mimeType: DriveMimeTypes['document'] }>
  | (Omit<Shortcut, 'shortcutDetails'> & {
      shortcutDetails: Omit<Shortcut['shortcutDetails'], 'targetMimeType'> & {
        targetMimeType: DriveMimeTypes['document'];
      };
    });

export const isAShortcut = (file: DriveFile | undefined): file is Shortcut =>
  file ? file.mimeType === mimeTypes.shortcut : false;

export const isAWordDocument = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      (isAShortcut(file) &&
        file.shortcutDetails.targetMimeType ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    : false;

export const isAMarkDownFile = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'text/markdown' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'text/markdown')
    : false;

export const isAPDF = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/pdf' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'application/pdf')
    : false;

export const isADrawing = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.google-apps.drawing' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'application/vnd.google-apps.drawing')
    : false;

export const isAForm = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.google-apps.form' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'application/vnd.google-apps.form')
    : false;

export const inlineEditingEnabled = (wiki: DriveFile | undefined, file: DriveFile | undefined) =>
  wiki?.properties?.showInlineEditing === 'true' && Boolean(file?.capabilities?.canEdit) && !isAForm(file);

export const isADocument = (file: DriveFile | undefined): file is Document =>
  file
    ? file.mimeType === mimeTypes.document ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === mimeTypes.document)
    : false;

export const isAPowerpoint = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.ms-powerpoint' ||
      file.mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      (isAShortcut(file) &&
        (file.shortcutDetails.targetMimeType === 'application/vnd.ms-powerpoint' ||
          file.shortcutDetails.targetMimeType ===
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'))
    : false;

export const isASlide = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.google-apps.presentation' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'application/vnd.google-apps.presentation')
    : false;

export const isASheet = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === 'application/vnd.google-apps.spreadsheet' ||
      file.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      (isAShortcut(file) &&
        (file.shortcutDetails.targetMimeType === 'application/vnd.google-apps.spreadsheet' ||
          file.shortcutDetails.targetMimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'))
    : false;

export const isAFolder = (
  file: DriveFile | undefined
): file is Extract<DriveFile, { mimeType: DriveMimeTypes['folder'] }> | ShortcutFolder =>
  file
    ? file.mimeType === 'application/vnd.google-apps.folder' ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === 'application/vnd.google-apps.folder')
    : false;

export const isAFile = (file: DriveFile) =>
  file.mimeType !== 'application/vnd.google-apps.folder' &&
  (!('shortcutDetails' in file) || file.shortcutDetails.targetMimeType !== 'application/vnd.google-apps.folder');

export const getViewInDriveUrl = (file: DriveFile | undefined, email?: string, wikiId?: string) => {
  const targetId = isAFolder(file) ? file.id : (file?.parents?.[0] ?? wikiId);
  if (!targetId) return undefined;
  const authUser = email ? `?authuser=${email}` : '';
  return `https://drive.google.com/drive/folders/${targetId}${authUser}`;
};

export const isAVideo = (file: DriveFile | undefined) =>
  file
    ? file.mimeType.startsWith('video/') ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType.startsWith('video/'))
    : false;

export const isAnImage = (file: DriveFile | undefined) =>
  file
    ? file.mimeType.startsWith('image/') ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType.startsWith('image/'))
    : false;

export function getSharedDriveId(files: DriveFile | DriveFile[]) {
  if (Array.isArray(files)) {
    const file = files.find((o) => 'driveId' in o);
    if (!file) return undefined;
    return 'driveId' in file ? file.driveId : undefined;
  }
  const file = files;
  if ('driveId' in file) {
    return file.driveId;
  }
  return undefined;
}

export const isExportable = (file: DriveFile | undefined) => {
  if (file?.id.startsWith(newFileId)) return false;
  const mimeType = isAShortcut(file) ? file.shortcutDetails.targetMimeType : file?.mimeType;
  const fileType = Object.keys(mimeTypes).find((k) => mimeTypes[k] === mimeType);
  return fileType ? fileType in exportFormats : false;
};

export const isPageExport = (file: DriveFile | undefined) => {
  if (file?.id.startsWith(newFileId)) return false;
  const mimeType = isAShortcut(file) ? file.shortcutDetails.targetMimeType : file?.mimeType;
  return mimeType ? pageExportMimeTypes.includes(mimeType) : false;
};

const isMarkdownFile = (file: DriveFile | undefined) =>
  file
    ? file.mimeType === mimeTypes.markdown ||
      (isAShortcut(file) && file.shortcutDetails.targetMimeType === mimeTypes.markdown)
    : false;

export const shouldShowEditButton = (file: DriveFile | undefined) =>
  isADocument(file) || isMarkdownFile(file) || isASheet(file) || isASlide(file);

export const getMimeType = (file: DriveFile | undefined) =>
  isAShortcut(file) ? file.shortcutDetails.targetMimeType : file?.mimeType;

export const getMimeTypeNameFromFile = (file: DriveFile | undefined) => {
  if (!file) return undefined;
  const mimeType = getMimeType(file);
  const fileType = Object.keys(mimeTypes).find((k) => mimeTypes[k] === mimeType);
  if (DEV) {
    if (!fileType) {
      log.info(file);
      log.error(`No mimeType found for ${mimeType}`);
    }
  }
  return fileType as keyof typeof mimeTypes;
};

export const getMimeTypeName = (mimeType: DriveMimeType | undefined) => {
  const fileType = Object.keys(mimeTypes).find((k) => mimeTypes[k] === mimeType);
  if (DEV) {
    if (!fileType) {
      log.info(mimeType);
      throw new Error(`No mimeType found for ${mimeType}`);
    }
  }
  return fileType;
};

export const getFileTypeName = (file: DriveFile) => {
  if (isAFolder(file)) return 'folder';
  if (isMarkdownLink(file)) return 'link';
  if (isASlide(file)) return 'slide';
  if (isAForm(file)) return 'form';
  if (isASheet(file)) return 'sheet';
  return 'page';
};

export const uuid = () => crypto.randomUUID();

export const getExportMimeType = (mimeType: DriveMimeType) => {
  const fileType = Object.keys(mimeTypes).find((k) => mimeTypes[k] === mimeType);
  if (!fileType) return undefined;
  const exportMimeType = exportFormats[fileType as keyof typeof exportFormats];
  return exportMimeType;
};

// https://gomakethings.com/preventing-cross-site-scripting-attacks-when-using-innerhtml-in-vanilla-javascript
/**
 * Escapes html to make it safe to display as text
 * Does not allow any HTML tags, everything becomes text
 */
export function sanitizeHTML(str: string) {
  const t = document.createElement('div');
  t.textContent = str;
  return t.innerHTML;
}

export const focusInput = (node: HTMLInputElement | null, select = true) => {
  queueMicrotask(() => {
    if (node) {
      node.focus();
      if (select) {
        node.select();
        node.scrollLeft = 0;
      }
    }
  });
};

export const scrollToTop = (node: HTMLElement | null) =>
  queueMicrotask(() => {
    if (node) {
      node.scrollTop = 0;
    }
  });

export const scrollToBottom = (node: HTMLElement | null) =>
  queueMicrotask(() => {
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  });

export const hasAPublicPermission = (file: DriveFile | undefined): boolean => {
  if (!file) return false;
  if (file.fileDetails?.permissions?.some((permission) => permission.type === 'anyone')) return true;
  if (!file.driveId && !file.permissions) return true;
  return file.permissions?.some((permission) => permission.type === 'anyone') ?? false;
};

// export class reg {
//   public static regex = {
//     checkbox: /\{\{checkbox\}\}/,
//   };
//   public static global = {
//     checkbox: new RegExp(macros.regex.checkbox.source, 'g'),
//   };
// }

// export const reg = {
//   style: /<style[^>]*>[^<]*<\/style>/gi,
//   br: /<br>/g,
//   tags: /<[^>]*>?/g,
//   nbsp: /&nbsp;/g,
//   spaces: /\s+/g,
//   lines: /\r?\n|\r/g,
//   code: //g,
//   // fontCheck: /@import url\((https:\/\/themes\.googleusercontent\.com\/fonts\/css\?kit=([\w-]+))\);/,
//   // fontSize: /font-size:(\d+)/,
//   // isURL,
//   // isEmail: /^[\w\-.]+@([\w-]+\.)+[\w-]{2,4}$/,
//   // https://regex101.com/r/7nfbOx/1

//   // // https://regex101.com/r/u8jOdA/1
//   // // We don't want to remove iframe's, video's, image's, etc.
//   isEmpty: /<\/?(html|head|meta|body|p|span|table|div)[^>]*>/g,
//   tab: /<p[^>]*class="title"[^>]*><span[^>]*>Tab 1<\/span><\/p>/,
//   title: /<p[^>]*class="title"[^>]*><span[^>]*>[^<]*<\/span><\/p>/g,
// };

export class reg {
  public static regex = {
    tab: /<p[^>]*class="title"[^>]*><span[^>]*>Tab 1<\/span><\/p>/,
    firstTab: /^<p[^>]*class="title"[^>]*><span[^>]*>Tab 1<\/span><\/p>/,
    isEmail: /^[\w\-.]+@([\w-]+\.)+[\w-]{2,}$/,
  };
  public static global = {
    br: /<br>/g,
    code: //g,
    isEmpty: /<\/?(html|head|meta|body|p|span|table|div)[^>]*>/g,
    iframe: /<iframe.*\/iframe>/g,
    nbsp: /&nbsp;/g,
    lines: /\r?\n|\r/g,
    spaces: /\s+/g,
    style: /<style[^>]*>[^<]*<\/style>/gi,
    tags: /<[^>]*>?/g,
    title: /<p[^>]*class="title"[^>]*><span[^>]*>[^<]*<\/span><\/p>/g,
    base64Image: /<img([^>]*)\ssrc="(data:image\/[^;]+;base64,[^"]+)"([^>]*)>/g,
  };
}

export const isEmpty = (html: string | undefined) => {
  if (!html) return true;
  const text = html.replace(reg.global.isEmpty, '').trim();
  return !text;
};

export const decodeHTML = (html: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

export const cleanHTML = (html: string, spaces = true) => {
  const result = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(reg.global.iframe, '')
    .replace(reg.global.style, '')
    .replace(reg.global.br, ' ')
    .replace(reg.global.tags, spaces ? ' ' : '')
    .replace(reg.global.nbsp, ' ')
    .replace(reg.global.lines, ' ')
    .replace(/&[a-z\d]{2,32};/gi, (entity) => entityMap[entity] ?? entity)
    .replace(/&#60418;/g, '')
    .replace(/&#60419;/g, '')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(reg.global.spaces, ' ')

    // Remove dynamic tags {{ }}
    .replace(/\{\{files\}\}/g, '')
    .trim();
  return result;
};

const parser = isBun ? undefined : new DOMParser();

export const getTextFromFile = (
  file: DriveFile & { data?: { html?: string; text?: string } },
  lowerCase = true
): string => {
  if (!('data' in file)) return '';
  const { data } = file;
  if (data.text) return data.text;
  if (!data.html) return '';
  if (file.mimeType === mimeTypes.pdf) return file.data.html?.toLowerCase() ?? '';
  if (isMarkdownLink(file)) return '';
  const html = getTextFromHTML(data.html, lowerCase);
  return html;
};

/**
 *
 * @param html the html string to parse
 * @param lowerCase whether to return the text in lowercase
 * @param spaces whether to replace tags with spaces (needed for search results)
 */
export const getTextFromHTML = (html: string, lowerCase = true, spaces = true): string => {
  if (!html) return '';

  // Clean the html, parse it, then return textContent
  // Accounts for &lt; and &gt; output instead of < and > from Docs
  // Provides the best support for unicode characters
  const cleanedHTML = cleanHTML(html, spaces);

  // In non-browser environments, return cleanedHTML
  if (!parser) return lowerCase ? cleanedHTML.toLowerCase() : cleanedHTML;

  // Check for remaining html entities or utf-8 characters
  // if not found return cleanedHTML
  const hasHTML = /&[a-z]{2,32};|&#\d{2,5};/i.test(cleanedHTML);
  if (!hasHTML) return lowerCase ? cleanedHTML.toLowerCase() : cleanedHTML;

  if (DEV) {
    // Print out all the entities
    const entities = cleanedHTML.match(/&[a-z]{2,32};|&#\d{2,5};/gi);
    log.info(Array.from(new Set(entities)));

    log.error('cleanHTML - Unhandled HTML entities:', Array.from(new Set(entities)));
    log.info(cleanedHTML);
  }
  const doc = parser.parseFromString(cleanedHTML, 'text/html');
  const text = doc.body.textContent;
  return (lowerCase ? text?.toLowerCase() : text) || '';
};

type Parameters = {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  access_type?: 'online' | 'offline'; // Not included in docs, but required to get refresh token
  state?: string;
  include_granted_scopes?: string;
  login_hint?: string;
  prompt?: 'none' | 'consent' | 'select_account';
};

// Scopes
// https://developers.google.com/identity/protocols/oauth2/scopes

/// TODO: check the security of including origin in state
// https://github.com/googleapis/google-api-nodejs-client/issues/685#issuecomment-702619121
// https://stackoverflow.com/a/63409239/1845423
const login_hint = hasWindow ? gup('email') : false;
export const getAuthURL = (redirect: string) => {
  const paramsObj: Parameters = {
    client_id: '1060883813950-ku2t78clneeeqvhvv4d8lna6k28n3q23.apps.googleusercontent.com',
    redirect_uri: `${redirect}/api/auth/google/callback`,
    response_type: 'code',
    access_type: 'offline',
    scope: defaultScope,

    // include_granted_scopes: 'true', // Disabled: previously granted 'drive' permission can trigger error screen
    // scope: 'email profile',
    // state: origin,
    // prompt: 'select_account',
    // prompt: 'consent',
    prompt: window.location.search === '?prompt=consent' ? 'consent' : 'select_account',
    ...(login_hint && typeof login_hint === 'string' && { login_hint }),
  };
  const params = new URLSearchParams(paramsObj).toString();
  const authURL = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  return authURL;
};

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue };
type JSONObject =
  | {
      [k: string]: JSONValue;
    }
  | JSONValue[];

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

// Small localStorage library for JSON objects
// Inspired by this API: https://www.npmjs.com/package/store
export const ls = {
  clear: () => {
    localStorage.clear();
  },
  assign: (key: string, data: JSONObject) => {
    if (Array.isArray(data)) {
      localStorage.setItem(key, JSON.stringify(data));
      return;
    }
    const str = localStorage.getItem(key);
    const existingData = str ? JSON.parse(str) : undefined;
    const existingValue = typeof existingData === 'object' ? existingData : {};
    const json = JSON.stringify({
      ...existingValue,
      ...data,
    });
    localStorage.setItem(key, json);
  },
  set: <Data extends JSONValue>(key: string, data: Data) => {
    if (typeof data === 'string') {
      localStorage.setItem(key, data);
      return;
    }

    try {
      const json = JSON.stringify(data);
      localStorage.setItem(key, json);
    } catch {
      /* empty */
    }
  },
  get: <Data extends JSONValue>(key: string) => {
    let str: string | null;
    try {
      str = localStorage.getItem(key);
    } catch {
      return null;
    }

    if (str) {
      try {
        return JSON.parse<Nullable<Data>>(str);
      } catch {
        return str as unknown as Nullable<Data>;
      }
    }

    return null;
  },
  remove: (key: string) => {
    localStorage.removeItem(key);
  },
};

export const getParentFolders = (files: DriveFile[], parentId: string): DriveFile[] => {
  const file = files.find(
    (o) =>
      o.id === parentId ||
      (o.mimeType === 'application/vnd.google-apps.shortcut' && o.shortcutDetails?.targetId === parentId)
  );
  if (!file) return [];
  return concatTo([file], getParentFolders(files, file.parents?.[0] ?? ''));
};

export const isInputEvent = <T>(e: KeyboardEvent | React.KeyboardEvent<T>) => {
  const isElement = e.target instanceof HTMLElement;
  if (isElement && (e.target.nodeName === 'INPUT' || e.target.nodeName === 'TEXTAREA')) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return true;
    return true;
  }
  if (isElement && e.target.classList.contains('MuiMenuItem-root')) {
    return true;
  }
  if (isElement && e.target.getAttribute('role') === 'menuitem') {
    return true;
  }
  if (isElement && e.target.contentEditable === 'true') {
    return true;
  }
  if (isModalEvent(e)) {
    return true;
  }
  return false;
};

export const isNullable = (value: unknown): value is null | undefined => value === null || value === undefined;

export const isModalEvent = <T>(e: KeyboardEvent | React.KeyboardEvent<T>) => {
  if (
    e.target instanceof Element &&
    (e.target.parentElement?.role === 'dialog' || e.target.closest('[role="dialog"]'))
  ) {
    return true;
  }
  return false;
};

// Define non-global for `test` and global for `replace`
// https://stackoverflow.com/q/1520800/1845423
const macroPatterns = {
  files: /<p[^>]*><span[^>]*>\{\{files\}\}<\/span><\/p>|\{[^{]*\{files\}[^}]*\}/,
  checkbox: /\{\{checkbox\}\}/,
  checkedbox: /\{\{checkbox\}\}\{\{checked\}\}/,
};

export abstract class macros {
  public static test = macroPatterns;
  public static global = {
    files: new RegExp(macroPatterns.files.source, 'g'),
    checkbox: new RegExp(macroPatterns.checkbox.source, 'g'),
    checkedbox: new RegExp(macroPatterns.checkedbox.source, 'g'),
  };
}

const partialMatchIndex = (content: string, input: string) => {
  const i = content.indexOf(input);
  const i2 = content.indexOf(input.slice(0, -1));
  const i3 = content.indexOf(input.slice(1));
  const index = i === -1 ? (i2 === -1 ? i3 : i2) : i;
  return { index, isPartialMatch: i === -1 };
};

export const getRange = ({
  input,
  name,
  content,
  range = 21,
  rangeBefore,
  rangeAfter,
}: {
  input: string;
  name: string;
  content: string;
  range?: number;
  rangeBefore?: number;
  rangeAfter?: number;
}) => {
  const before = rangeBefore ?? range;
  const after = rangeAfter ?? range;

  // Title match
  const nameLower = name.toLowerCase();
  const inputLower = input.toLowerCase();
  const contentLower = content.toLowerCase();
  const nameIndex = nameLower.indexOf(inputLower);
  const title = name.slice(nameIndex, nameIndex + input.length);
  const titleBefore = name.slice(0, nameIndex);
  const titleAfter = name.slice(nameIndex + input.length);

  // Content match
  const { index: contentIndex, isPartialMatch } = partialMatchIndex(contentLower, inputLower);
  const match = content.slice(contentIndex, contentIndex + input.length + (isPartialMatch ? -1 : 0));
  const beforeIndex =
    contentIndex + before > content.length
      ? contentIndex - before * 2
      : contentIndex - before < 0
        ? 0
        : contentIndex - before;
  const textBefore = content.slice(beforeIndex, contentIndex);
  const textAfter = content.slice(contentIndex + input.length + (isPartialMatch ? -1 : 0), contentIndex + after);

  return {
    title,
    titleBefore,
    titleAfter,
    nameIndex,
    textBefore,
    match,
    textAfter,
  };
};

export const toLastModifiedTime = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const diffYears = Math.floor(diff / (1000 * 3600 * 24 * 365));

  // Early return for years
  if (diffYears >= 1) {
    return new Date(Date.now() - diff).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  // if (diffYears > 0) {
  //   return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  // }

  const diffMonths = Math.floor(diff / (1000 * 3600 * 24 * 30));
  const diffDays = Math.floor(diff / (1000 * 3600 * 24));
  const diffHours = Math.floor(diff / (1000 * 3600));
  const diffMinutes = Math.floor(diff / (1000 * 60));
  const diffSeconds = Math.floor(diff / 1000);
  if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  }
  if (diffDays > 0) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  if (diffSeconds > 30) {
    return 'less than a minute ago';
  }
  return 'just now';
};

export const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  // If it's a single word with a period, replace with a space
  if (!str.includes(' ') && str.includes('.')) {
    return str
      .split('.')
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(' ');
  }
  return str
    .split(' ')
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(' ');
};

export const readingTime = (text: string) => {
  // https://scholarwithin.com/average-reading-speed#adult-average-reading-speed
  const wpm = 238;
  const noOfWords = text.trim().split(/\s+/).length;
  const minutes = noOfWords / wpm;
  const readTime = Math.ceil(minutes);

  if (readTime === 60) {
    return '1 hour read';
  }
  if (readTime > 60) {
    const hours = Math.floor(readTime / 60);
    const minutes = readTime % 60;
    return `${hours} hour${hours > 1 ? 's' : ''}, ${minutes} min read`;
  }

  return readTime + ' min read';
};

/**
 *
 * @param url https://lh3.googleusercontent.com/a/ACg8ocIcIn35jglKWYnlFYscQtH_2hoMv_yznpQjNoI2IkZFKMi3QzdC=s64
 * @param size
 */
export const resizePhoto = (url: string | undefined, size: number) => {
  if (!url) return '';
  return url.replace(/=s\d+/, `=s${size}`);
};

/**
 * It's possible for a user to have both auth/drive.file and drive scopes at the same time
 */
export const isFileScope = (scope?: string): boolean => {
  if (fileScope) return true;
  const s = scope ?? gapi.client.getToken()?.scope;
  if (import.meta.env.DEV) {
    if (!s && gapi.client.getToken()?.access_token && MODERN) log.error('scope is undefined');
  }
  return !!s?.includes('drive.file') && !/drive\s/.test(s) && !s.endsWith('drive');
};

export const isTokenFileScope = (token: PartialToken | null): token is PartialToken => {
  if (fileScope) return true;
  return !!token && !!isFileScope(token?.scope);
};

// export const request = async <T>(url: string, options: RequestInit): Promise<T> => {
//   const res = await fetch(url, options);
//   if (!res.ok) {
//     throw new Error(res.statusText);
//   }
//   return res.json();
// }

export const getNewFileId = () => `${newFileId}-${uuid()}`;

export const getNewFile = (
  params: Params['create'] & {
    shortcutDetails?: {
      targetId: string;
      targetMimeType?: DriveMimeType;
    };
  } & {
    user: Profile | undefined;
  }
): DriveFile => {
  const { user, properties, mimeType, ...options } = params;
  const base = {
    id: `${newFileId}-${uuid()}`,
    ...options,
    ...(properties && { properties }),
    trashed: false,
    createdTime: new Date().toISOString(),
    modifiedTime: new Date().toISOString(),
    webViewLink: '',
    webContentLink: '',
    ownedByMe: true,
    size: '0',
    ...(user && {
      lastModifyingUser: {
        displayName: user.name,
        photoLink: user.image,
        me: true,
      },
      owners: [
        {
          displayName: user.name,
          photoLink: user.image,
          emailAddress: user.email,
        },
      ],
    }),
  } satisfies Omit<DriveFile, 'mimeType'>;

  if (mimeType === mimeTypes.shortcut) {
    if (!options.shortcutDetails?.targetMimeType) throw new Error('targetMimeType is required for shortcut');
    return {
      ...base,
      mimeType: mimeTypes.shortcut,
      ...(options.shortcutDetails && {
        shortcutDetails: {
          targetId: options.shortcutDetails.targetId,
          targetMimeType: options.shortcutDetails.targetMimeType as Exclude<
            DriveFile['mimeType'],
            'application/vnd.google-apps.shortcut'
          >,
        },
      }),
    } satisfies DriveFile;
  }

  return {
    ...base,
    mimeType,
  } satisfies Exclude<DriveFile, { mimeType: typeof mimeTypes.shortcut }>;
};

export const safeParse = <T = unknown>(str: string) => {
  try {
    return JSON.parse<T>(str);
  } catch {
    return undefined;
  }
};

export const safeURL = (url: string | undefined) => {
  try {
    return new URL(url ?? 'about:blank');
  } catch {
    return new URL('about:blank');
  }
};

export const round = (num: number, precision = 0) => {
  const factor = 10 ** precision;
  return Math.round(num * factor) / factor;
};

export const get = <V, T = unknown, K extends string = string>(obj: T, key?: K) => {
  if (!key) {
    if (typeof obj === 'object' && obj) return obj as V;
    return undefined;
  }
  const keys = key.split('.');
  let result: unknown = obj;
  if (typeof obj !== 'object') return undefined;

  for (const k of keys) {
    if (result && typeof result === 'object') {
      result = (result as Record<string, unknown>)[k];
    }
  }
  return result as V;
};

type GetProperty<T, K extends PropertyKey> =
  T extends Record<K, infer V> ? (V extends never ? undefined : V) : undefined;

export const getObjValue = <T, K extends PropertyKey>(obj: T | undefined, key: K): GetProperty<T, K> => {
  if (!obj || typeof obj !== 'object') return undefined as GetProperty<T, K>;

  const value = (obj as Record<PropertyKey, unknown>)[key];
  return value as GetProperty<T, K>;
};

export const getIndexPage = (file: DriveFile, files: DriveFile[]) => {
  return isAFolder(file) ? files?.find((o) => o.parents?.[0] === file?.id && o.name === indexName) : undefined;
};

export const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
  // Submit form on enter if there are no inputs
  if (e.key === 'Enter' && e.target instanceof HTMLFormElement) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.dispatchEvent(new Event('submit', { bubbles: true }));
  }
};

export const removeURLParams = (url: string) => {
  if (!url) return url;
  const urlObj = new URL(url);
  urlObj.searchParams.delete('usp');
  return urlObj.toString();
};

export function capitalize(str = '') {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function capitalizeName(str = '') {
  return str
    .split(' ')
    .map((s) => capitalize(s))
    .join(' ');
}

export const legacyClick = MODERN
  ? undefined
  : (e: React.MouseEvent<HTMLAnchorElement>) => {
      const href = e.currentTarget.getAttribute('href');
      if (!href) {
        log.error('No href found for element', e.currentTarget.nodeName);
        return;
      }

      const isExternal = href.includes('://');
      if (isExternal) return;

      e.preventDefault();
      window.legacyHistory?.push(href);
    };

export const getFileDepth = (file: DriveFile, files: DriveFile[]): number | undefined => {
  const parent = files.find((f) => getId(f) === file.parents?.[0]);
  if (!parent) {
    if (files.some((f) => getId(f) === file.id)) return 0;
    return undefined;
  }
  return 1 + (getFileDepth(parent, files) ?? 0);
};

export const getTotalDepth = (files: DriveFile[]) => {
  const depths = files.map((file) => getFileDepth(file, files) ?? 0);
  return depths.length ? Math.max(...depths) : 0;
};

/**
 * If the total depth surpasses the file depth we can assume we are no longer loading
 */
export const hasFolderLoadedByDepth = (file: DriveFile | undefined, files: DriveFile[]) => {
  if (!file) return false;
  const fileDepth = getFileDepth(file, files);
  if (fileDepth === undefined) return false;
  const loadedDepth = getTotalDepth(files);
  if (loadedDepth > fileDepth) return true;
  return false;
};

/**
 * Used for client rendering
 */
export const toQueryString = (o: Record<string, string | boolean | undefined>) => {
  const params = new URLSearchParams();
  for (const key in o) {
    if (o[key] !== undefined) {
      params.append(key, `${o[key]}`);
    }
  }
  const str = params.toString();
  if (str.length === 0) return undefined;
  return `?${str.replaceAll('=true', '')}`;
};

/**
 * Used for sending requests to server
 */
export const toQueryParams = (o: Record<string, string | boolean | undefined>) => {
  const params = new URLSearchParams();
  for (const key in o) {
    if (o[key] !== undefined) {
      params.append(key, `${o[key]}`);
    }
  }
  const str = params.toString();
  if (str.length === 0) return '';
  return `?${str}`;
};

export const getLegacyState = LEGACY
  ? () => {
      // Legacy workaround
      let state: { workspaceId: string; workspace: { id: string } } | undefined;
      // @ts-expect-error - legacy workaround
      // oxlint-disable-next-line
      window.setState((s) => (state = s));
      return state;
    }
  : () => undefined;

// logging
// debugPrevNext({ file, files, wiki });
// Get testing values for getPrev/getNext
export const debugPrevNext = ({
  file,
  files,
  wiki,
}: {
  file: DriveFile | undefined;
  files: DriveFile[];
  wiki: DriveFile | undefined;
}) => {
  log.info({
    name: '',
    input: {
      files: files.map((o) => ({
        id: o.id,
        name: o.name,
        mimeType: o.mimeType,
        parents: o.parents,
        properties: o.properties,
        ...(isAShortcut(o) ? { shortcutDetails: o.shortcutDetails } : {}),
      })),
      file: {
        id: file?.id,
        name: file?.name,
        mimeType: file?.mimeType,
        parents: file?.parents,
        properties: file?.properties,
        ...(isAShortcut(file) ? { shortcutDetails: file.shortcutDetails } : {}),
      },
      wiki: {
        id: wiki?.id,
        name: wiki?.name,
        mimeType: wiki?.mimeType,
        ...(isAShortcut(wiki) ? { shortcutDetails: wiki.shortcutDetails } : {}),
      },
    },
    output: {},
  });
};

export const setCookie = (options: CookieOptions) => {
  if ('cookieStore' in window) {
    void window.cookieStore.set(options);
    return;
  }
  const { name, value, ...opts } = options;
  let updatedCookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);

  for (const optionKey in opts) {
    let optionValue = (opts as Record<string, string | number | boolean | Date>)[optionKey];
    if (optionKey === 'expires') {
      optionValue = new Date(optionValue as number).toUTCString();
    }
    updatedCookie += '; ' + optionKey + (optionValue === true ? '' : '=' + String(optionValue));
  }
  document.cookie = updatedCookie;
};

export const deleteCookie = (name: string, path = '/') => {
  if ('cookieStore' in window) {
    void window.cookieStore.delete({ name, path });
    return;
  }
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

export const toThrow = (message: string) => {
  throw new Error(message);
};

export class MutationQueue<T> {
  private queue = new Map<string, { current: Promise<T>; next?: () => Promise<T>; nextPromise?: Promise<T> }>();

  async run(id: string, mutation: () => Promise<T>) {
    const queue = this.queue.get(id);
    if (!queue) {
      const promise = mutation().finally(() => !this.queue.get(id)?.next && this.queue.delete(id));
      this.queue.set(id, { current: promise });
      return promise;
    }

    const isIntermediate = !!queue.next;
    queue.next = mutation;
    if (!queue.nextPromise) {
      const runNext = () => {
        const q = this.queue.get(id);
        this.queue.delete(id);
        return q?.next?.() ?? mutation();
      };
      queue.nextPromise = queue.current.then(runNext, runNext);
    }

    const promise = isIntermediate ? queue.nextPromise : queue.current;
    return promise.catch(() => queue.nextPromise);
  }
}

export function sample<T>(arr: readonly [T, ...T[]]): T;
export function sample<T>(arr: readonly T[]): T | undefined;
export function sample<T>(arr: readonly T[]): T | undefined {
  const l = arr?.length;
  if (!l) return undefined;
  return arr[Math.floor(Math.random() * l)];
}

export const getNewFileMessage = () => {
  const d = new Date();
  if (d.getMonth() === 11 && d.getDate() <= 25) {
    return (
      sample(['🎁', '🎄', '❄️', '☃️', '⛄️', '🥳', '🛷', '🎅', '🏂', '⛷️']) +
      ' ' +
      sample([
        'Joyous',
        'Festive',
        'Delightful',
        'Wonderful',
        'Jolly',
        'Wondrous',
        'Majestic',
        'Wonderful',
        'Splendorous',
        'Enchanting',
      ]) +
      '! You created'
    );
  }
  if (d.getMonth() === 9) {
    return (
      sample([
        '🎃',
        '👻',
        '⚰️',
        '💀',
        '☠️',
        '🦉',
        '🦇',
        '🕸',
        '😱',
        '🎃',
        '🪦',
        '🧙‍♀️',
        '🕷',
        '🧛‍♂️',
        '🌙',
        '🔮',
        '🕯',
        '🩸',
        '🧟‍♂️',
        '🐈‍⬛',
        '🥀',
      ]) +
      ' ' +
      sample([
        'Spooky',
        'Boo!',
        'Mysterious',
        'Eerie',
        'Petrifying',
        'Horrifying',
        'Spine-chilling',
        'Chilling',
        'Scary',
        'Frightening',
        'Unnerving',
        'Ghoulish',
        'Haunting',
        'Bone-chilling',
        'Nightmarish',
        'Ghostly',
        'Bewitching',
        'Sinister',
        'Its alive!',
      ]) +
      '...' +
      ' You ' +
      sample(['summoned', 'awakened', 'conjured up', 'unleashed', 'spawned', 'summoned'])
    );
  }

  return (
    sample(['🚀', '😎', '✨', '🔥', '🎉', '🍾', '👏', '🎈', '💪', '⚡️', '👍', '🙌', '🌟', '☀️', '💫', '💎']) +
    ' ' +
    sample([
      'Nice',
      'Brilliant',
      'Excellent',
      'Exceptional',
      'Fantastic',
      'Super',
      'Awesome',
      'Magnificent',
      'Wonderful',
      'Amazing',
      'Impressive',
      'Cool',
      'Great',
      'Marvelous',
      'Wondrous',
      'Splendid',
      'Superb',
      'Spectacular',
      'Sensational',
      'Outstanding',
      'Groovy',
      'Terrific',
    ]) +
    '! You created'
  );
};

export class FixedSizeMap<K, V> extends Map<K, V> {
  private limit: number;
  constructor(limit: number) {
    super();
    this.limit = limit;
  }
  override set(key: K, value: V): this {
    if (this.size >= this.limit && !this.has(key)) {
      const k = this.keys().next().value;
      if (k) this.delete(k);
    }
    return super.set(key, value);
  }
}

export const columnToLetter = (colNum: number) => {
  let letter = '';
  let col = colNum;
  while (col > 0) {
    const remainder = (col - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    col = Math.floor((col - 1) / 26);
  }
  return letter;
};

// https://stackoverflow.com/a/36183085/1845423
export async function base64ToBlobAsync(base64: string): Promise<string> {
  const blob = await fetch(base64).then((res) => res.blob());
  return URL.createObjectURL(blob);
}
